import { useId } from 'react';
import { LuInfo } from 'react-icons/lu';

import { getApiErrorMessage } from '~api/client';
import {
  ColourLegend,
  ErrorMessage,
  Loader,
  SideBar,
  ToggleSwitch,
  TooltipContent,
  TooltipRoot,
  TooltipTrigger,
} from '~components';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~components/ui/select';
import { useBlastDesignContext } from '../context';
import { BenchLevelSelect } from './BenchLevelSelect';
import { BlockModelCard } from './BlockModelCard';
import { BlockPropertySelect } from './BlockPropertySelect';
import { DrilledHolePropertySelect } from './DrilledHolePropertySelect';
import { DrilledHolesInfo } from './DrilledHolesInfo';
import { NeighbouringDrillHolesSection } from './NeighbouringDrillHolesSection';
import { PlannedOrActualSelect } from './PlannedOrActualSelect';

/* -------------------------------------------------------------------------- */
/*                               Sub-components                               */
/* -------------------------------------------------------------------------- */

const SectionHeader = ({ text }: { text: string }) => (
  <div className="flex items-center justify-between">
    <h2 className="font-bold text-foreground text-xl">{text}</h2>
  </div>
);

/** Toggle switch for showing/hiding block colour labels on the canvas. */
const BlockLabelsToggle = ({
  show,
  onChange,
  disabled,
}: {
  show: boolean;
  onChange: (v: boolean) => void;
  disabled: boolean;
}) => {
  const id = useId();

  return (
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-1">
        <label className="font-medium text-sm leading-none" htmlFor={id}>
          Show block colour labels
        </label>
        <TooltipRoot>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help">
              <LuInfo size={14} className="text-muted-foreground" aria-label="Label info" />
            </span>
          </TooltipTrigger>
          <TooltipContent side="top">
            Labels are only available for categorical properties (e.g. Hardness, Strand). Numeric gradients do not have
            labels.
          </TooltipContent>
        </TooltipRoot>
      </span>
      <ToggleSwitch
        id={id}
        checked={show && !disabled}
        onCheckedChange={onChange}
        disabled={disabled}
        ariaLabel="Show block colour labels on canvas"
      />
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * Sidebar for the Blast Design Context page.
 * Displays block model controls, drilled holes controls, and colour legends.
 *
 * @example
 * ```tsx
 * <BlastDesignContextProvider>
 *   <BlastDesignContextSideBar />
 * </BlastDesignContextProvider>
 * ```
 */
const BlastDesignContextSideBar = () => {
  const {
    benchLevel,
    setBenchLevel,
    blocksQuery,
    setSelectedModelName,
    selectedBlockProperty,
    setSelectedBlockProperty,
    blockColourScale,
    showBlockLabels,
    setShowBlockLabels,
    drilledDataQuery,
    plannedOrActual,
    setPlannedOrActual,
    selectedHoleProperty,
    setSelectedHoleProperty,
    drilledHoleColourScale,
    layers,
  } = useBlastDesignContext();

  const selectedBlockModel = blocksQuery.data?.block_model;
  const blockModels = blocksQuery.data?.metadata?.block_models || [];
  const blocksErrorMessage = getApiErrorMessage(blocksQuery.error);
  const drilledDataErrorMessage = getApiErrorMessage(drilledDataQuery.error);

  return (
    <SideBar className="w-100 pt-6">
      <BenchLevelSelect selected={benchLevel} onChange={setBenchLevel} />

      {/* Block Model Section */}
      {layers.blocks.isVisible && (
        <>
          <SectionHeader text="Block Model" />
          {blocksQuery.isLoading ? (
            <Loader orientation="horizontal">Building blast design context ...</Loader>
          ) : blocksErrorMessage ? (
            <ErrorMessage
              className="rounded-md p-2"
              collapsible={true}
              intent="error"
              title="Error loading blocks"
              message={blocksErrorMessage}
              handleAction={() => blocksQuery.refetch()}
            />
          ) : (
            selectedBlockModel && (
              <>
                <Select value={selectedBlockModel.name} onValueChange={setSelectedModelName}>
                  <SelectTrigger className="w-full text-sm">
                    <SelectValue placeholder="Select Layer" />
                  </SelectTrigger>
                  <SelectContent>
                    {blockModels.map((model) => (
                      <SelectItem key={model.name} value={model.name}>
                        {model.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <BlockModelCard blockModel={selectedBlockModel} />
              </>
            )
          )}
          <BlockPropertySelect
            selectedBlockProperty={selectedBlockProperty}
            onBlockPropertyChange={setSelectedBlockProperty}
          />
          <BlockLabelsToggle
            show={showBlockLabels}
            onChange={setShowBlockLabels}
            disabled={blockColourScale?.type === 'continuous'}
          />
          {blockColourScale && <ColourLegend colourScale={blockColourScale} />}

          {/* Divider */}
          <div className="my-2 flex flex-h-1 border-b border-b-border" />
        </>
      )}

      {/* Neighbouring Drill Holes Section */}
      {layers.neighbouringDrillHoles.isVisible && (
        <>
          <NeighbouringDrillHolesSection />

          {/* Divider */}
          <div className="my-2 flex flex-h-1 border-b border-b-border" />
        </>
      )}

      {/* Drilled Holes Section */}
      {layers.holes.isVisible && (
        <>
          <SectionHeader text="Drilled Holes" />
          {drilledDataQuery.isLoading && <Loader orientation="horizontal">Loading drill holes ...</Loader>}
          {drilledDataErrorMessage && (
            <ErrorMessage
              className="rounded-md p-2"
              collapsible={true}
              intent="error"
              title="Error loading drilled holes"
              message={drilledDataErrorMessage}
              handleAction={() => drilledDataQuery.refetch()}
            />
          )}
          {drilledDataQuery.data?.drilled_data && (
            <>
              <PlannedOrActualSelect selected={plannedOrActual} onChange={setPlannedOrActual} />
              <DrilledHolesInfo drilledHoles={drilledDataQuery.data.drilled_data} />
              <DrilledHolePropertySelect
                selectedHoleProperty={selectedHoleProperty}
                onHolePropertyChange={setSelectedHoleProperty}
              />
              {drilledHoleColourScale && <ColourLegend colourScale={drilledHoleColourScale} />}
            </>
          )}
        </>
      )}
    </SideBar>
  );
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { BlastDesignContextSideBar };
